import React from 'react';
import clsx from 'clsx';
import {
  BorderStyle,
  BorderWidth,
  Colors,
  Duration,
  FontSize,
  FontWeight,
  Opacity,
  Radius,
  Spacing,
} from '@common/tokens';
import { ErrorHandler } from '@common/components';
import { ButtonContent, type ButtonSizeType } from './_button-content';

type ButtonKindType = 'primary' | 'secondary' | 'tertiary' | 'dashed' | 'link';
type ButtonShapeType = 'default' | 'circle' | 'round';

export function Button({
  id,
  kind = 'secondary',
  primary = false,
  size = 'lg',
  shape = 'default',
  danger = false,
  ghost = false,
  block = false,
  disabled = false,
  loading = false,
  icon,
  iconRight,
  htmlType = 'button',
  className,
  onClick,
  children,
  ariaLabel,
  dataTestId,
  ...props
}: {
  id?: string;
  kind?: ButtonKindType;
  primary?: boolean;
  size?: ButtonSizeType;
  shape?: ButtonShapeType;
  danger?: boolean;
  ghost?: boolean;
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  htmlType?: 'button' | 'submit' | 'reset';
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  children?: React.ReactNode;
  ariaLabel?: string;
  dataTestId?: string;
}) {
  const iconOnly = (!!icon || !!iconRight) && !children;

  if (iconOnly && !ariaLabel) {
    console.error(
      'Button: icon-only buttons should have an ariaLabel for accessibility.'
    );
    ErrorHandler.showErrorNotification();
    return null;
  }

  const cls = clsx(
    'wk-button',
    (primary || kind === 'primary') && 'wk-button--primary',
    kind !== 'secondary' && kind !== 'primary' && `wk-button--${kind}`,
    size !== 'lg' && `wk-button--${size}`,
    shape !== 'default' && `wk-button--${shape}`,
    {
      'wk-button--danger': danger,
      'wk-button--ghost': ghost,
      'wk-button--block': block,
      'wk-button--loading': loading,
      'wk-button--disabled': disabled,
      'wk-button--icon-only': iconOnly,
    },
    className
  );

  const handleClick = (e: React.MouseEvent) => {
    if (disabled || loading) {
      // A swallowed click must be fully inert: without stopPropagation it
      // bubbles past the button and fires clickable ancestors (cards, rows)
      // the consumer's own onClick would normally guard against.
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
  };

  return (
    <>
      <button
        {...props}
        id={id}
        className={cls}
        type={htmlType}
        data-testid={dataTestId}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-busy={loading ? 'true' : undefined}
        onClick={handleClick}
      >
        <ButtonContent
          icon={icon}
          iconRight={iconRight}
          iconOnly={iconOnly}
          loading={loading}
          size={size}
        >
          {children}
        </ButtonContent>
      </button>
      <style jsx>{`
        .wk-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          border: ${BorderWidth.sm} ${BorderStyle.solid} transparent;
          border-radius: ${Radius.sm};
          font-weight: ${FontWeight.bold};
          font-family: inherit;
          line-height: 1;
          cursor: pointer;
          text-decoration: none;
          transition: all ${Duration.normal};
          box-shadow: none;
          outline: none;
          white-space: nowrap;
          user-select: none;
          padding: 0 ${Spacing.md}px;
          height: ${Spacing.xxl}px;
          font-size: ${FontSize.md};
          background-color: ${Colors.white};
          border-color: ${Colors.elmLighter};
          color: ${Colors.elm};
        }
        .wk-button:hover,
        .wk-button:focus-visible {
          border-color: ${Colors.elm};
          color: ${Colors.elm};
        }
        .wk-button:active {
          border-color: ${Colors.elm};
          color: ${Colors.elm};
          box-shadow: 0 0 0 3px ${Colors.elmLightest};
        }
        .wk-button:focus-visible {
          outline: ${BorderWidth.md} ${BorderStyle.solid} ${Colors.elm};
          outline-offset: ${Spacing.xxs}px;
        }

        .wk-button--sm {
          height: ${Spacing.lg}px;
          padding: 0 ${Spacing.sm}px;
          font-size: ${FontSize.sm};
        }
        .wk-button--md {
          height: ${Spacing.xl}px;
          padding: 0 ${Spacing.md}px;
          font-size: ${FontSize.sm};
        }

        .wk-button--circle {
          border-radius: ${Radius.full};
          padding: 0;
        }
        .wk-button--round {
          border-radius: 40px;
        }

        .wk-button--icon-only {
          aspect-ratio: 1;
          padding: 0;
        }
        .wk-button--block {
          display: flex;
          width: 100%;
        }

        .wk-button--primary {
          background-color: ${Colors.elm};
          border-color: ${Colors.elm};
          color: ${Colors.white};
        }
        .wk-button--primary:hover,
        .wk-button--primary:focus-visible {
          background-color: ${Colors.elm};
          border-color: ${Colors.elm};
          color: ${Colors.white};
          opacity: ${Opacity.high};
        }
        .wk-button--primary:active {
          background-color: ${Colors.elmDarkest};
          border-color: ${Colors.elmDarkest};
          color: ${Colors.white};
        }

        .wk-button--dashed {
          background-color: ${Colors.white};
          border-color: ${Colors.elmLighter};
          border-style: ${BorderStyle.dashed};
          color: ${Colors.elm};
        }
        .wk-button--dashed:hover,
        .wk-button--dashed:focus-visible {
          border-color: ${Colors.elm};
          color: ${Colors.elm};
        }
        .wk-button--dashed:active {
          border-color: ${Colors.elmDarker};
          color: ${Colors.elmDarker};
        }

        .wk-button--link {
          display: inline-block;
          background-color: transparent;
          border-color: transparent;
          color: ${Colors.elm};
          padding: 0;
          line-height: 20px;
          height: auto;
        }
        .wk-button--link:hover,
        .wk-button--link:focus-visible {
          color: ${Colors.elmDarker};
          border-color: transparent;
        }
        .wk-button--link:active {
          color: ${Colors.elmDarkest};
          border-color: transparent;
        }

        .wk-button--tertiary {
          background-color: transparent;
          border-color: transparent;
          color: ${Colors.elm};
        }
        .wk-button--tertiary:hover,
        .wk-button--tertiary:focus-visible {
          color: ${Colors.elmDarker};
        }
        .wk-button--tertiary:active {
          color: ${Colors.elmDarkest};
        }

        .wk-button--danger {
          border-color: ${Colors.coralLightest};
          color: ${Colors.coral};
        }
        .wk-button--danger:hover,
        .wk-button--danger:focus-visible {
          border-color: ${Colors.coral};
          color: ${Colors.coral};
        }
        .wk-button--danger:active {
          border-color: ${Colors.coralDarkest};
          color: ${Colors.coralDarkest};
        }

        .wk-button--primary.wk-button--danger {
          background-color: ${Colors.coral};
          border-color: ${Colors.coral};
          color: ${Colors.white};
        }
        .wk-button--primary.wk-button--danger:hover,
        .wk-button--primary.wk-button--danger:focus-visible {
          background-color: ${Colors.coralDarker};
          border-color: ${Colors.coralDarker};
          color: ${Colors.white};
        }
        .wk-button--primary.wk-button--danger:active {
          background-color: ${Colors.coralDarkest};
          border-color: ${Colors.coralDarkest};
          color: ${Colors.white};
        }

        .wk-button--link.wk-button--danger {
          color: ${Colors.coral};
          border-color: transparent;
        }
        .wk-button--link.wk-button--danger:hover,
        .wk-button--link.wk-button--danger:focus-visible {
          color: ${Colors.coralDarker};
          border-color: transparent;
        }
        .wk-button--link.wk-button--danger:active {
          color: ${Colors.coralDarkest};
          border-color: transparent;
        }

        .wk-button--tertiary.wk-button--danger {
          color: ${Colors.coral};
          border-color: transparent;
        }
        .wk-button--tertiary.wk-button--danger:hover,
        .wk-button--tertiary.wk-button--danger:focus-visible {
          color: ${Colors.coral};
          border-color: ${Colors.coralLighter};
        }
        .wk-button--tertiary.wk-button--danger:active {
          color: ${Colors.coralDarkest};
        }

        .wk-button--ghost {
          background-color: transparent;
          border-color: ${Colors.white};
          color: ${Colors.white};
        }
        .wk-button--ghost:hover,
        .wk-button--ghost:focus-visible {
          border-color: ${Colors.white};
          color: ${Colors.white};
          opacity: ${Opacity.high};
        }

        .wk-button--primary.wk-button--ghost,
        .wk-button--primary.wk-button--ghost:hover,
        .wk-button--primary.wk-button--ghost:focus-visible,
        .wk-button--primary.wk-button--ghost:active {
          background-color: transparent;
          border-color: ${Colors.elm};
          color: ${Colors.elm};
        }

        .wk-button--ghost.wk-button--danger,
        .wk-button--ghost.wk-button--danger:hover,
        .wk-button--ghost.wk-button--danger:focus-visible,
        .wk-button--ghost.wk-button--danger:active {
          background-color: transparent;
          border-color: ${Colors.coral};
          color: ${Colors.coral};
        }

        .wk-button--disabled,
        .wk-button--disabled:hover,
        .wk-button--disabled:focus-visible,
        .wk-button--disabled:active,
        .wk-button:disabled,
        .wk-button:disabled:hover,
        .wk-button:disabled:focus-visible,
        .wk-button:disabled:active {
          background-color: ${Colors.steelLightest};
          border-color: ${Colors.steel};
          color: ${Colors.midnightLightest};
          /* No pointer-events: none here: it removes the button from
             hit-testing, so clicks retarget to clickable ancestors (and the
             not-allowed cursor never shows). The native disabled attribute
             already swallows clicks on its own. */
          cursor: not-allowed;
        }

        .wk-button--link.wk-button--disabled,
        .wk-button--link.wk-button--disabled:hover,
        .wk-button--link.wk-button--disabled:focus-visible,
        .wk-button--link.wk-button--disabled:active,
        .wk-button--link:disabled,
        .wk-button--link:disabled:hover,
        .wk-button--link:disabled:focus-visible,
        .wk-button--link:disabled:active {
          background-color: transparent;
          border-color: transparent;
        }

        /* Borderless like the link kind, so disabling one must not grow a box
           around text that never had one. The grey text still reads disabled. */
        .wk-button--tertiary.wk-button--disabled,
        .wk-button--tertiary.wk-button--disabled:hover,
        .wk-button--tertiary.wk-button--disabled:focus-visible,
        .wk-button--tertiary.wk-button--disabled:active,
        .wk-button--tertiary:disabled,
        .wk-button--tertiary:disabled:hover,
        .wk-button--tertiary:disabled:focus-visible,
        .wk-button--tertiary:disabled:active {
          background-color: transparent;
          border-color: transparent;
        }

        .wk-button--loading {
          cursor: default;
          opacity: ${Opacity.high};
        }

        .wk-button--primary:active:not(.wk-button--link):not(
            .wk-button--tertiary
          ) {
          box-shadow: 0 0 10px 3px ${Colors.elm}20;
        }
        .wk-button--danger:active:not(.wk-button--link) {
          box-shadow: 0 0 10px 3px ${Colors.coral}20;
        }
        .wk-button--ghost:active {
          box-shadow: 0 0 10px 3px ${Colors.white}20;
        }
        .wk-button--link:active {
          box-shadow: none;
        }
      `}</style>
    </>
  );
}
